import * as z from 'zod'

const settingsCurveTypeSchema = z.enum([
    // https://mui.com/x/react-charts/lines/#interpolation
    'catmullRom',
    'linear',
    'monotoneX',
    'monotoneY',
    'natural',
    'step',
    'stepBefore',
    'stepAfter',
    'bumpY',
    'bumpX',
])

const settingsIssueGridColsSchema = z.enum([
    'issueLabel',
    'issueType',
    'shareName',
    'tags',
    'status',
    'date',
    'offeredSize',
    'subscribedSize',
    'financialAdvisors',
    'totalCommitment',
    'outcome',
    'priceChange',
    'updatedAt',
    'noteIndicator',
    'sector',
    'marketplace',
    'preMoney',
    'subscriptionPrice',
    'closePrice',
    'marketcap',
    'legalAdvisors',
    'issuingAgents',
    'discount10dVwap',
    'terpDiscount10dVwap',
    'dilutionMax',
    'dilutionActual',
    'feeLevel1',
    'warrants',
    'noncashConsideration',
])
const settingsCompanyGridColsSchema = z.enum([
    'country',
    'name',
    'sector',
    'marketList',
    'capSize',
    'latestIssue',
    'financialAdvisors',
    'legalAdvisors',
    'issuingAgents',
])

const settingsTradesGraphLineSeriesSchema = z.enum(['closePrice', 'vwap', 'marketcap', 'numberOfShares'])
const settingsTradesGraphBarSeriesSchema = z.enum(['splitAdjustedQuantity', 'turnoverSek', 'tradedProportion'])

export const userSettingsSchema = z.object({
    company: z
        .object({
            historyOrder: z.enum(['desc', 'asc']).optional(),
        })
        .optional(),
    issueForm: z
        .object({
            showAiSuggestions: z.boolean().optional(),
        })
        .optional(),
    issuesGrid: z
        .object({
            columnOrder: z
                .partialRecord(
                    z.enum(['current', 'previous', 'future']),
                    z.array(settingsIssueGridColsSchema).nullable(),
                )
                .optional()
                .nullable(),
            columnVisibility: z
                .partialRecord(
                    z.enum(['current', 'previous', 'future']),
                    z.partialRecord(settingsIssueGridColsSchema, z.boolean().optional()).nullable(),
                )
                .optional()
                .nullable(),
        })
        .optional(),
    companiesGrid: z
        .object({
            columnOrder: z
                .partialRecord(z.enum(['default']), z.array(settingsCompanyGridColsSchema).nullable())
                .optional()
                .nullable(),
            columnVisibility: z
                .partialRecord(
                    z.enum(['default']),
                    z.partialRecord(settingsCompanyGridColsSchema, z.boolean().optional()).nullable(),
                )
                .optional()
                .nullable(),
        })
        .optional(),
    rightsCalculator: z
        .object({
            amount: z.number().nullable().optional(),
            percentage: z.number().nullable().optional(),
        })
        .optional(),
    tradesGraph: z
        .object({
            curveType: settingsCurveTypeSchema.nullable().optional(),
            serie: settingsTradesGraphLineSeriesSchema.nullable().optional(),
            barSerie: settingsTradesGraphBarSeriesSchema.nullable().optional(),
        })
        .optional(),
    ui: z
        .object({
            language: z.enum(['sv', 'en']).nullable().optional(),
        })
        .optional(),
})

export type IssueGridCols = z.infer<typeof settingsIssueGridColsSchema>
export type CompanyGridCols = z.infer<typeof settingsCompanyGridColsSchema>

export type AvailableTradesGraphLineSeries = z.infer<typeof settingsTradesGraphLineSeriesSchema>
export type AvailableTradesGraphBarSeries = z.infer<typeof settingsTradesGraphBarSeriesSchema>

export type IssuesGridColumnSettingsName = 'current' | 'previous' | 'future'
export type CompaniesGridColumnSettingsName = 'default'

export type UserSettings = z.infer<typeof userSettingsSchema>
export type UserSettingsResponse = {
    company: {
        historyOrder: 'asc' | 'desc'
    }
    issueForm: {
        showAiSuggestions: boolean
    }
    issuesGrid: {
        columnOrder: Partial<Record<IssuesGridColumnSettingsName, IssueGridCols[] | null>>
        columnVisibility: Partial<Record<IssuesGridColumnSettingsName, Partial<Record<IssueGridCols, boolean>> | null>>
    }
    companiesGrid: {
        columnOrder: Partial<Record<CompaniesGridColumnSettingsName, CompanyGridCols[] | null>>
        columnVisibility: Partial<
            Record<CompaniesGridColumnSettingsName, Partial<Record<CompanyGridCols, boolean>> | null>
        >
    }
    rightsCalculator: {
        amount: number | null
        percentage: number | null
    }
    tradesGraph: {
        curveType: // https://mui.com/x/react-charts/lines/#interpolation
            | 'catmullRom'
            | 'linear'
            | 'monotoneX'
            | 'monotoneY'
            | 'natural'
            | 'step'
            | 'stepBefore'
            | 'stepAfter'
            | 'bumpY'
            | 'bumpX'
            | null
        serie: AvailableTradesGraphLineSeries | null
        barSerie: AvailableTradesGraphBarSeries | null
    }
    ui: {
        language: 'sv' | 'en' | null
    }
}
